import React, { useEffect, useMemo } from "react";
import iphone from "../assets/iphone.png";
import ContinueButton from "../components/Buttons/ContinueButton";
import { Button } from "@chakra-ui/react";
import { Navigate, useNavigate } from "react-router-dom";
import LogoAndText from "@/components/LogoAndText";
import { tokenStorage } from "../api/tokenStorage";
import { useQuery } from "@tanstack/react-query";
import { GetUserInfoResponse } from "@shared_types";
import { axiosInstance } from "@/api/Api";
import { LoadingPage } from "./LoadingPage";
import { wasJustCreated } from "./RedirectPage";
import { usePostHog } from "posthog-js/react";

const StartPage: React.FC = () => {
  const nonEssentialImages = useMemo(() => [iphone], []);
  const nav = useNavigate();
  const posthog = usePostHog();

  // Warm the browser cache for non-essential images (e.g. the hero iPhone)
  // after mount so they're ready by the time the user scrolls/navigates.
  useEffect(() => {
    nonEssentialImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [nonEssentialImages]);

  const userInfoQuery = useQuery({
    queryKey: ["AuthQueryLoginPage"],
    retry: 0,
    queryFn: async () => {
      const authToken = tokenStorage.getToken();
      const response = await axiosInstance.get<GetUserInfoResponse>(
        "/login/status",
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      return response.data;
    },
  });
  // If the user is logged out we will get id 0, hence we
  // dont want to be redirecting
  let userInfo: GetUserInfoResponse | undefined =
    userInfoQuery.data || undefined;
  if (userInfoQuery.data && userInfoQuery.data.id == 0) {
    userInfo = undefined;
  }
  const recentlyCreated =
    userInfo != undefined && wasJustCreated(userInfo.joinDate, 1800);

  const shouldRedirect =
    userInfo != undefined &&
    !userInfo.isTemp &&
    !userInfo.isTempTrial &&
    !userInfo.isTempPremium &&
    !recentlyCreated;

  const isTrialOrPremium =
    userInfo != undefined && (userInfo.isTrial || userInfo.isPremium);

  const isPageLoading = tokenStorage.isTokenSet() && userInfoQuery.isPending;

  useEffect(() => {
    try {
      posthog.reset();
    } catch (err) {
      console.error("Failed to reset posthog:", err);
    }
  }, [posthog]);

  // Only wait for essential images to show the page
  if (isPageLoading) return <LoadingPage />;

  if (shouldRedirect || isTrialOrPremium) {
    return <Navigate to="/home/learn" />;
  }

  if (
    recentlyCreated &&
    userInfo &&
    !userInfo.isPremium &&
    !userInfo.isTrial &&
    !userInfo.isTemp &&
    !userInfo.isTempTrial &&
    !userInfo.isTempPremium
  ) {
    return <Navigate to="/onboarding" />;
  }

  const handleContinue = () => {
    nav("/onboarding");
  };

  const handleLogin = () => {
    nav("/login");
  };

  return (
    <div className="appPadding flex flex-col h-full w-full overflow-hidden items-center z-0 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 mt-14">
        <LogoAndText />
        <div className="w-72 text-center text-sm mt-4 flex flex-col pb-4">
          <span>Done with Duolingo? </span>
          <span>Start speaking real Español.</span>
        </div>
        <div
          className="inline-block px-3 py-1.5 rounded-full bg-blue-50/70 text-[var(--color-blue)] text-sm"
          style={{
            boxShadow:
              "0 1px 4px -1px rgba(0,122,255,0.12), inset 0 1px 0px 0px rgba(255,255,255,0.6)",
          }}
        >
          <div className="flex flex-row items-center justify-center gap-1.5 text-[var(--color-blue)]">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                // iOS AppRating renders all 5 stars filled with brand blue.
                <svg
                  key={i}
                  className="w-[14px] h-[14px]"
                  viewBox="0 0 24 24"
                  fill="var(--color-blue)"
                >
                  <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
                </svg>
              ))}
            </div>
            <div className="leading-tight">
              <span className="font-medium">4.9</span>
              <span className="text-[var(--color-blue)]/70 text-sm">
                {" "}
                • App Store Rating
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 flex flex-col justify-end items-center w-full z-10 p-2 pb-4">
        <ContinueButton
          onClick={handleContinue}
          text="Get started"
        />
        <Button
          variant="ghost"
          onClick={handleLogin}
          color={"var(--color-grey)"}
          height="var(--button-height)"
          fontSize="base"
          className={`w-full text-center rounded-xl break-words`}
          _hover={{ bg: "transparent" }}
          _active={{ bg: "transparent" }}
        >
          I already have an account
        </Button>
      </div>
    </div>
  );
};

export default StartPage;
